import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import TestimonialsCarousel from './components/TestimonialsCarousel';

export default function ProductoRaizPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user } = useAuth();
  const [hasPurchased, setHasPurchased] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) setMenuOpen(false);
  }, [scrolled]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  useEffect(() => {
    const checkPurchase = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_purchases')
        .select('*')
        .eq('user_id', user.id)
        .in('product_slug', ['producto-raiz', 'raiz', 'despierta-tu-voz'])
        .single();

      if (!error && data) {
        setHasPurchased(true);
      }
      setLoading(false);
    };

    checkPurchase();
  }, [user]);

  const handleScrollToSection = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePurchase = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const contactSection = document.getElementById('contacto');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navLinks = [
    { href: '/#rituales', label: 'Rituales gratuitos' },
    { href: '/#metodo', label: 'Un método propio' },
    { href: '/#espacio', label: 'Despierta tu voz' },
    { href: '/#sobre-mi', label: 'La voz detrás' },
    { href: '/#contacto', label: 'Contacto' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center transform hover:scale-105 transition-transform duration-300">
              <img
                src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d3c9f2d28e8d17a63b0e1d543ffef045.png"
                alt="Una voz más que propia"
                className="h-14 sm:h-16 w-auto"
              />
            </Link>

            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center gap-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-sm font-medium transition-all duration-300 whitespace-nowrap hover:scale-110 cursor-pointer text-gray-700 hover:text-[#D6D57B]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="hidden lg:flex items-center">
              <Link
                to="/auth/login"
                className="px-5 py-2.5 bg-[#A7A86C] text-white rounded-full text-sm font-semibold hover:bg-[#8A8B5C] transition-all duration-300 whitespace-nowrap shadow-md hover:shadow-lg flex items-center gap-2 transform hover:scale-105"
              >
                <i className="ri-user-line"></i>
                Mi espacio propio
              </Link>
            </div>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 text-gray-700 hover:bg-gray-100"
              aria-label="Abrir menú"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        <div
          className={`lg:hidden fixed inset-0 top-20 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
            menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setMenuOpen(false)}
        />

        <div
          className={`lg:hidden fixed top-20 left-0 right-0 bg-white shadow-xl transition-all duration-300 transform ${
            menuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'
          }`}
        >
          <div className="px-6 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 text-gray-700 hover:text-[#D6D57B] hover:bg-[#D6D57B]/10 rounded-lg transition-all duration-200 font-medium"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/auth/login"
              className="flex items-center justify-center gap-2 px-5 py-3 bg-[#A7A86C] text-white rounded-full font-semibold hover:bg-[#8A8B5C] transition-all duration-300 shadow-md hover:shadow-lg mt-4"
            >
              <i className="ri-user-line"></i>
              Mi espacio propio
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-[#F9F7F4] to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D6D57B]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D6D57B]/5 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-[#D6D57B]/10 rounded-full">
                <span className="w-2 h-2 bg-[#D6D57B] rounded-full animate-pulse"></span>
                <span className="text-[#D6D57B] text-sm font-semibold">ETAPA 1 • RITUAL RAÍZ</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-gray-900 mb-6 leading-tight">
                Raíz
              </h1>

              <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
                Conecta con tu <strong>fuente creativa más auténtica.</strong> Una experiencia de meditación guiada + escritura para desbloquear las historias que solo tú puedes contar.
              </p>

              <div className="inline-block mb-10 px-6 py-3 bg-white rounded-2xl shadow-md border border-gray-100">
                <p className="text-lg text-gray-600 italic">
                  Deja de buscar las palabras "correctas". <strong className="text-[#D6D57B] not-italic">Encuentra las tuyas.</strong>
                </p>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-[#D6D57B] text-xl"></i>
                  <span>Acceso ilimitado</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-refresh-line text-[#D6D57B] text-xl"></i>
                  <span>Repite cuando quieras</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-download-line text-[#D6D57B] text-xl"></i>
                  <span>Materiales descargables</span>
                </div>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#D6D57B]/20 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/1835c0e52db77dc92514edcc01b6e4db.jpg"
                  alt="Ritual Raíz"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section - 3 Phases Method */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#D6D57B]/10 rounded-full">
              <i className="ri-award-line text-[#D6D57B]"></i>
              <span className="text-[#D6D57B] text-sm font-semibold">MÉTODO COMPLETO EN 3 FASES</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Un viaje de transformación creativa
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Un <strong>sistema completo de 3 fases</strong> que conecta tu voz interior con el papel. Desde la meditación profunda hasta tu primera historia terminada.
            </p>
          </div>

          {/* Phase 1 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#F9F7F4] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#D6D57B] to-[#A7A86C] rounded-2xl text-white font-bold text-xl shadow-lg">
                      1
                    </div>
                    <div>
                      <span className="text-xs text-[#D6D57B] font-semibold uppercase tracking-wider">Fase 1</span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">Mente inconsciente</h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Accedemos a tu <strong>imaginario personal</strong> a través de la relajación profunda. Aquí es donde viven las historias que aún no has contado.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-bookmark-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Tarjetas de intención</h4>
                        <p className="text-sm text-gray-600">Prepara tu mente y establece tu propósito creativo antes de la inmersión</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-headphone-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Meditación guiada a 432 Hz</h4>
                        <p className="text-sm text-gray-600">30-45 min de relajación profunda con frecuencia sanadora para acceder a tu inconsciente</p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#D6D57B] font-medium">
                          <i className="ri-music-2-line"></i> Frecuencia 432 Hz • Sentada/Relajación
                        </span>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-map-2-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Mapa de ensoñación ilustrado</h4>
                        <p className="text-sm text-gray-600">Captura las imágenes, símbolos y sensaciones que emerjan durante tu viaje interior</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-64 lg:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Ethereal%20dreamy%20woman%20with%20closed%20eyes%20in%20deep%20meditation%20surrounded%20by%20soft%20golden%20light%20floating%20abstract%20symbols%20ancient%20letters%20and%20mystical%20imagery%20representing%20unconscious%20mind%20and%20inner%20world%20exploration%20warm%20sepia%20tones%20artistic%20painterly%20style&width=600&height=700&seq=raiz-phase1-v2&orientation=portrait"
                    alt="Fase 1: Acceso a la mente inconsciente"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#F9F7F4] via-transparent to-transparent lg:from-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 2 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#F9F7F4] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto order-2 lg:order-1">
                  <img
                    src="https://readdy.ai/api/search-image?query=Artistic%20scene%20of%20woman%20writing%20in%20beautiful%20journal%20with%20golden%20pen%20surrounded%20by%20floating%20words%20and%20illuminated%20pages%20consciousness%20awakening%20creative%20writing%20moment%20warm%20amber%20lighting%20soft%20focus%20artistic%20documentary%20style&width=600&height=700&seq=raiz-phase2-v2&orientation=portrait"
                    alt="Fase 2: Hacer consciente lo inconsciente"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-[#F9F7F4] via-transparent to-transparent lg:from-transparent"></div>
                </div>

                <div className="p-10 lg:p-12 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#D6D57B] to-[#A7A86C] rounded-2xl text-white font-bold text-xl shadow-lg">
                      2
                    </div>
                    <div>
                      <span className="text-xs text-[#D6D57B] font-semibold uppercase tracking-wider">Fase 2</span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">Hacer consciente</h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Transformamos lo que emergió en <strong>material creativo tangible</strong>. Aquí es donde tus visiones se convierten en palabras.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-volume-up-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Audio de escritura guiada</h4>
                        <p className="text-sm text-gray-600">Una guía sonora especial que te acompaña mientras escribes, manteniendo el estado creativo</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-book-3-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Cuaderno creativo</h4>
                        <p className="text-sm text-gray-600">Plantillas y espacios diseñados para capturar y organizar tu material creativo emergente</p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs text-[#D6D57B] font-medium">
                          <i className="ri-file-download-line"></i> Descargable en PDF
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Phase 3 */}
          <div className="mb-12">
            <div className="bg-gradient-to-br from-[#F9F7F4] to-white rounded-3xl border border-gray-100 overflow-hidden shadow-lg hover:shadow-xl transition-all">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-10 lg:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#D6D57B] to-[#A7A86C] rounded-2xl text-white font-bold text-xl shadow-lg">
                      3
                    </div>
                    <div>
                      <span className="text-xs text-[#D6D57B] font-semibold uppercase tracking-wider">Fase 3</span>
                      <h3 className="text-2xl md:text-3xl font-serif text-gray-900">Encarnar tu voz</h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Ejercicios específicos de <strong>escritura creativa</strong> para convertir tu material en herramientas literarias que usarás siempre.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-book-open-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Campo semántico propio</h4>
                        <p className="text-sm text-gray-600">Ejercicios para identificar y expandir las palabras que solo tú usas de esa manera única</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-user-heart-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Arquetipos y simbología</h4>
                        <p className="text-sm text-gray-600">Descubre los símbolos recurrentes de tu imaginario y aprende a usarlos conscientemente</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#D6D57B]/20 rounded-lg flex-shrink-0">
                        <i className="ri-mic-line text-[#D6D57B] text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Tu voz narrativa única</h4>
                        <p className="text-sm text-gray-600">Ejercicios para definir y fortalecer el tono que te distingue de cualquier otra escritora</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative h-64 lg:h-auto">
                  <img
                    src="https://readdy.ai/api/search-image?query=Empowered%20woman%20writer%20at%20vintage%20desk%20with%20completed%20manuscript%20pages%20floating%20around%20her%20golden%20light%20emanating%20from%20her%20heart%20representing%20authentic%20voice%20and%20creative%20mastery%20warm%20earthy%20tones%20artistic%20inspirational%20style&width=600&height=700&seq=raiz-phase3-v2&orientation=portrait"
                    alt="Fase 3: Encarnar tu voz narrativa"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#F9F7F4] via-transparent to-transparent lg:from-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Value Box */}
          <div className="bg-gradient-to-r from-[#D6D57B]/10 via-[#D6D57B]/5 to-[#D6D57B]/10 rounded-3xl p-8 md:p-10 border border-[#D6D57B]/20">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-2">
                Todo esto incluido por solo <span className="text-[#D6D57B]">64€</span>
              </h3>
              <p className="text-gray-600">Pago único • Acceso inmediato • Tuyo para siempre</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#D6D57B]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D6D57B]/20 rounded-full mb-3">
                  <i className="ri-headphone-line text-[#D6D57B] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Meditación 432 Hz + Ondas Binaurales</p>
                <p className="text-sm text-gray-600 leading-relaxed">Frecuencia de sanación natural que sincroniza tu cerebro con estados creativos profundos</p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#D6D57B]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D6D57B]/20 rounded-full mb-3">
                  <i className="ri-volume-up-line text-[#D6D57B] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Audio de Escritura Guiada</p>
                <p className="text-sm text-gray-600 leading-relaxed">Exclusivo del ritual. Te acompaño paso a paso para hacer consciente tu material creativo</p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#D6D57B]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D6D57B]/20 rounded-full mb-3">
                  <i className="ri-map-2-line text-[#D6D57B] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Mapa de Ensoñación Ilustrado</p>
                <p className="text-sm text-gray-600 leading-relaxed">Herramienta única diseñada específicamente para capturar tu imaginario personal</p>
              </div>

              <div className="bg-white rounded-xl p-5 shadow-sm border border-[#D6D57B]/10">
                <div className="w-12 h-12 flex items-center justify-center bg-[#D6D57B]/20 rounded-full mb-3">
                  <i className="ri-book-open-line text-[#D6D57B] text-2xl"></i>
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">Cuaderno Creativo + Ejercicios</p>
                <p className="text-sm text-gray-600 leading-relaxed">Guía estructurada con múltiples ejercicios para trabajar arquetipos, simbología y desarrollar tu campo semántico único</p>
              </div>
            </div>

            <div className="text-center">
              {loading ? (
                <div className="px-10 py-4 bg-gray-200 rounded-full text-gray-500 font-semibold inline-block">
                  Cargando...
                </div>
              ) : hasPurchased ? (
                <Link
                  to="/contenido/raiz"
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#D6D57B] to-[#A7A86C] text-gray-900 rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Acceder al contenido</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </Link>
              ) : (
                <a
                  href="#contacto"
                  onClick={handlePurchase}
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#D6D57B] to-[#A7A86C] text-gray-900 rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Quiero empezar mi ritual Raíz</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-[#F9F7F4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-serif text-gray-900 mb-4">
              Por qué funciona
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#D6D57B] to-[#A7A86C] mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Card 1 */}
            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D6D57B]/30 to-[#D6D57B]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-lightbulb-flash-line text-4xl text-[#A7A86C]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Accede a tu imaginario <span className="text-[#A7A86C]">personal</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                No a fórmulas genéricas. Este ritual te conecta con las imágenes, símbolos y palabras que <strong className="text-gray-800">solo tú tienes</strong>.
              </p>
            </div>

            {/* Card 2 */}
            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D6D57B]/30 to-[#D6D57B]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-seedling-line text-4xl text-[#A7A86C]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Escribe desde <span className="text-[#A7A86C]">la raíz</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                No desde &quot;lo que debería decir&quot;. Conecta con tu <strong className="text-gray-800">verdad interior</strong> y deja que guíe tu escritura.
              </p>
            </div>

            {/* Card 3 */}
            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D6D57B]/30 to-[#D6D57B]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-headphone-line text-4xl text-[#A7A86C]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Audios que cambian cómo te <span className="text-[#A7A86C]">relacionas con tu escritura</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Una experiencia profunda pero accesible. <strong className="text-gray-800">No necesitas experiencia previa</strong> en meditación.
              </p>
            </div>

            {/* Card 4 */}
            <div className="group bg-white rounded-3xl p-10 shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D6D57B]/30 to-[#D6D57B]/10 rounded-2xl mb-8 group-hover:scale-110 transition-transform duration-500">
                <i className="ri-infinity-line text-4xl text-[#A7A86C]"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-4 leading-tight">
                Cada vez es una experiencia <span className="text-[#A7A86C]">diferente</span>
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                Es tuyo para siempre. Cada vez que lo uses, <strong className="text-gray-800">el inicio será distinto</strong>, revelando nuevas capas de tu voz creativa. También puedes explorarlo para <strong className="text-gray-800">dar voz a tus personajes</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Preview Section - Reorganized */}
      <section className="py-20 px-6 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#D6D57B]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#D6D57B]/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#D6D57B]/10 rounded-full">
              <i className="ri-eye-line text-[#D6D57B]"></i>
              <span className="text-[#D6D57B] text-sm font-semibold">DESCUBRE LO QUE INCLUYE</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Un vistazo al ritual
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Escucha un fragmento de la meditación y conoce los materiales que te acompañarán
            </p>
          </div>

          {/* Audio Preview - Centered */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-[#F9F7F4] to-white rounded-3xl p-8 border border-gray-100 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#D6D57B] to-[#A7A86C] rounded-xl shadow-md">
                  <i className="ri-headphone-line text-white text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-serif text-gray-900">Meditación guiada</h3>
                  <p className="text-sm text-gray-500">Fragmento de 20 segundos</p>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#D6D57B]/20 to-[#D6D57B]/5 rounded-full">
                    <i className="ri-music-2-line text-[#A7A86C] text-3xl"></i>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-1">Ritual Raíz - Meditación 432 Hz</p>
                    <p className="text-sm text-gray-500">Conecta con tu fuente creativa</p>
                  </div>
                </div>
                
                <audio 
                  controls 
                  className="w-full h-12 rounded-lg"
                  style={{ accentColor: '#A7A86C' }}
                >
                  <source src="https://static.readdy.ai/audio/meditation-preview-20s.mp3" type="audio/mpeg" />
                  Tu navegador no soporta el elemento de audio.
                </audio>
                
                <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-timer-line"></i>
                    Vista previa: 0:20
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    Duración completa: 35 min
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 italic text-center">
                "Cierra los ojos y permite que tu respiración te guíe hacia ese lugar donde habitan tus historias..."
              </p>
            </div>
          </div>

          {/* Materials Preview - 3 Columns */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Tarjetas de intención */}
            <div className="group bg-gradient-to-br from-[#F9F7F4] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Elegant%20intention%20cards%20deck%20with%20golden%20edges%20and%20mystical%20botanical%20illustrations%20on%20cream%20paper%20featuring%20moon%20phases%20and%20sacred%20geometry%20symbols%20minimalist%20spiritual%20design%20warm%20sepia%20tones%20product%20photography%20on%20marble%20surface&width=300&height=400&seq=tarjetas-intencion-mockup&orientation=portrait"
                      alt="Tarjetas de intención"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#D6D57B] rounded-full shadow-md">
                    <i className="ri-bookmark-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Tarjetas de intención</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Prepara tu mente antes de cada sesión. Cada tarjeta te guía para establecer tu propósito creativo.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#D6D57B] font-medium bg-[#D6D57B]/10 px-3 py-1 rounded-full">
                  <i className="ri-file-download-line"></i>
                  PDF descargable
                </span>
              </div>
            </div>

            {/* Cuaderno creativo */}
            <div className="group bg-gradient-to-br from-[#F9F7F4] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Beautiful%20creative%20writing%20journal%20with%20kraft%20paper%20cover%20botanical%20pressed%20flower%20illustrations%20and%20handwritten%20calligraphy%20title%20elegant%20minimalist%20design%20warm%20earthy%20tones%20product%20photography%20on%20wooden%20desk%20with%20dried%20flowers&width=300&height=400&seq=cuaderno-creativo-mockup&orientation=portrait"
                      alt="Cuaderno creativo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#D6D57B] rounded-full shadow-md">
                    <i className="ri-book-3-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Cuaderno creativo</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Plantillas diseñadas para capturar y organizar tu material creativo emergente.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#D6D57B] font-medium bg-[#D6D57B]/10 px-3 py-1 rounded-full">
                  <i className="ri-file-download-line"></i>
                  +30 páginas de ejercicios
                </span>
              </div>
            </div>

            {/* Mapa de ensoñación */}
            <div className="group bg-gradient-to-br from-[#F9F7F4] to-white rounded-3xl p-6 border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-500">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-5">
                  <div className="w-40 h-52 rounded-xl overflow-hidden shadow-lg transform group-hover:scale-105 transition-transform duration-500">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Illustrated%20dream%20map%20poster%20with%20watercolor%20celestial%20elements%20moon%20stars%20and%20flowing%20botanical%20vines%20on%20aged%20parchment%20paper%20mystical%20cartography%20style%20with%20empty%20spaces%20for%20writing%20warm%20golden%20sepia%20tones%20artistic%20stationery%20product&width=300&height=400&seq=mapa-ensonacion-mockup&orientation=portrait"
                      alt="Mapa de ensoñación"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 flex items-center justify-center bg-[#D6D57B] rounded-full shadow-md">
                    <i className="ri-map-2-line text-white text-lg"></i>
                  </div>
                </div>
                <h3 className="text-xl font-serif text-gray-900 mb-2">Mapa de ensoñación</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Captura las imágenes y símbolos que emerjan durante tu meditación en este mapa ilustrado único.
                </p>
                <span className="inline-flex items-center gap-1 text-xs text-[#D6D57B] font-medium bg-[#D6D57B]/10 px-3 py-1 rounded-full">
                  <i className="ri-palette-line"></i>
                  Ilustrado a mano
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contacto" className="py-24 px-6 bg-gradient-to-b from-white to-[#F9F7F4]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
            Empieza tu ritual Raíz hoy
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Conecta con tu fuente creativa más auténtica y descubre las historias que solo tú puedes contar
          </p>

          <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 mb-10">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-left">
                <div className="text-5xl font-bold text-[#D6D57B] mb-2">64€</div>
                <p className="text-gray-600">Pago único • Acceso inmediato</p>
                <p className="text-sm text-gray-500 mt-2">Acceso ilimitado para siempre</p>
              </div>

              {loading ? (
                <div className="px-10 py-4 bg-gray-200 rounded-full text-gray-500 font-semibold">
                  Cargando...
                </div>
              ) : hasPurchased ? (
                <Link
                  to="/contenido/raiz"
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#D6D57B] to-[#A7A86C] text-gray-900 rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Acceder al contenido</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </Link>
              ) : (
                <a
                  href="mailto:contacto@unavozmasquepropia.com?subject=Quiero%20comprar%20Ritual%20Raíz&body=Hola,%20estoy%20interesada%20en%20comprar%20el%20Ritual%20Raíz%20por%2064€."
                  className="group inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#D6D57B] to-[#A7A86C] text-gray-900 rounded-full text-lg font-semibold hover:shadow-xl transition-all whitespace-nowrap transform hover:scale-105"
                >
                  <span>Empezar mi ritual Raíz</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
                </a>
              )}
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#D6D57B] text-xl"></i>
              <span>Pago seguro</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-customer-service-line text-[#D6D57B] text-xl"></i>
              <span>Soporte personalizado</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-heart-line text-[#D6D57B] text-xl"></i>
              <span>Hecho con amor</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Carousel Section */}
      <section className="py-20 px-6 bg-[#F9F7F4] overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#D6D57B]/10 rounded-full">
              <i className="ri-chat-heart-line text-[#D6D57B]"></i>
              <span className="text-[#D6D57B] text-sm font-semibold">EXPERIENCIAS REALES</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
              Lo que dicen sobre Raíz
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Escritoras que ya han conectado con su voz más auténtica
            </p>
          </div>

          <TestimonialsCarousel />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#283748] text-white py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Link to="/" className="inline-block mb-6">
            <img
              src="https://static.readdy.ai/image/e4930f1328430401d33e8e8f8ac29155/d3c9f2d28e8d17a63b0e1d543ffef045.png"
              alt="Una voz más que propia"
              className="h-16 w-auto mx-auto"
            />
          </Link>
          <p className="text-white/70 mb-6">
            Encuentra tu voz única a través de la meditación y la escritura creativa
          </p>
          <div className="flex justify-center gap-6 mb-6">
            <a href="mailto:contacto@unavozmasquepropia.com" className="text-white/70 hover:text-white transition-colors">
              <i className="ri-mail-line text-2xl"></i>
            </a>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="text-white/50 text-sm">
              © 2026 Una voz más que propia. Todos los derechos reservados. •{' '}
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
